const REDACTED_VALUE = '••••••••••••';
const LEGACY_REDACTED_VALUES = new Set(['••••••••', '***']);
const LEGACY_MISSING_VALUES = new Set(['—', '---']);
const MAX_PREVIEW_ROWS = 50;

class CustomListPreview extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.csvUrl = (this.dataset.csvUrl || '').trim();
    this.initialRows = Math.max(1, Number.parseInt(this.dataset.initialRows || '5', 10) || 5);
    this.loadingLabel = this.dataset.loadingLabel || 'Loading preview...';
    this.errorLabel = this.dataset.errorLabel || 'The preview could not be displayed.';
    this.emptyLabel = this.dataset.emptyLabel || 'No preview is available.';
    this.expandLabel = this.dataset.expandLabel || 'Show all entries';
    this.collapseLabel = this.dataset.collapseLabel || 'Show fewer entries';
    this.protectedLabel = this.dataset.protectedLabel || 'Masked preview value';

    this.loadingElement = this.querySelector('[data-list-preview-loading]');
    this.noticeElement = this.querySelector('[data-list-preview-notice]');
    this.noticeTextElement = this.querySelector('[data-list-preview-notice-text]');
    this.tableWrapElement = this.querySelector('[data-list-preview-table-wrap]');
    this.tableElement = this.querySelector('[data-list-preview-table]');
    this.toggleElement = this.querySelector('[data-list-preview-toggle]');
    this.toggleLabelElement = this.querySelector('[data-list-preview-toggle-label]');

    this.toggleElement?.addEventListener('click', () => this.toggleRows());

    if (this.dataset.serverRendered === 'true') {
      this.enhanceServerRenderedTable();
      return;
    }

    if (!this.csvUrl) {
      this.showNotice(this.emptyLabel);
      return;
    }

    this.loadTable();
  }

  enhanceServerRenderedTable() {
    const extraRows = this.querySelectorAll('[data-preview-extra-row="true"]');
    extraRows.forEach((row) => {
      row.hidden = true;
    });

    if (this.toggleElement && extraRows.length > 0) {
      this.toggleElement.hidden = false;
    }
  }

  async loadTable() {
    this.showLoading();
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10000);

    try {
      const response = await fetch(this.csvUrl, {
        credentials: 'omit',
        mode: 'cors',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const csvText = (await response.text()).replace(/^\uFEFF/, '');
      const delimiter = this.resolveDelimiter(csvText);
      const rows = this.normalizeRows(this.parseCsv(csvText, delimiter));
      if (rows.length < 2) throw new Error('Empty preview CSV');

      this.renderTable(rows);
      this.hideFeedback();
    } catch (_error) {
      this.showNotice(this.errorLabel);
    } finally {
      window.clearTimeout(timeoutId);
      this.hideLoading();
    }
  }

  resolveDelimiter(csvText) {
    const sampleLines = String(csvText)
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .slice(0, 5);

    let semicolonScore = 0;
    let commaScore = 0;
    for (const line of sampleLines) {
      semicolonScore += this.countUnquotedCharacters(line, ';');
      commaScore += this.countUnquotedCharacters(line, ',');
    }
    return semicolonScore > commaScore ? ';' : ',';
  }

  countUnquotedCharacters(line, character) {
    let count = 0;
    let insideQuotes = false;
    for (let index = 0; index < line.length; index += 1) {
      const currentCharacter = line[index];
      if (currentCharacter === '"') {
        if (insideQuotes && line[index + 1] === '"') {
          index += 1;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (!insideQuotes && currentCharacter === character) {
        count += 1;
      }
    }
    return count;
  }

  parseCsv(csvText, delimiter) {
    const rows = [];
    let row = [];
    let value = '';
    let insideQuotes = false;

    for (let index = 0; index < csvText.length; index += 1) {
      const currentCharacter = csvText[index];
      const nextCharacter = csvText[index + 1];

      if (currentCharacter === '"') {
        if (insideQuotes && nextCharacter === '"') {
          value += '"';
          index += 1;
        } else {
          insideQuotes = !insideQuotes;
        }
      } else if (!insideQuotes && currentCharacter === delimiter) {
        row.push(value.trim());
        value = '';
      } else if (!insideQuotes && (currentCharacter === '\n' || currentCharacter === '\r')) {
        if (currentCharacter === '\r' && nextCharacter === '\n') index += 1;
        row.push(value.trim());
        rows.push(row);
        row = [];
        value = '';
      } else {
        value += currentCharacter;
      }
    }

    if (value.length > 0 || row.length > 0) {
      row.push(value.trim());
      rows.push(row);
    }
    return rows;
  }

  normalizeRows(rows) {
    const filteredRows = rows.filter((row) => row.some((cell) => cell !== ''));
    if (filteredRows.length === 0) return [];

    const columnCount = filteredRows[0].length;
    return filteredRows.map((row) => {
      const normalizedRow = row.slice(0, columnCount);
      while (normalizedRow.length < columnCount) normalizedRow.push('');
      return normalizedRow;
    });
  }

  renderTable(rows) {
    const tableHead = this.tableElement?.querySelector('thead');
    const tableBody = this.tableElement?.querySelector('tbody');
    if (!tableHead || !tableBody || !this.tableElement) return;

    tableHead.replaceChildren();
    tableBody.replaceChildren();
    const [headerRow, ...allBodyRows] = rows;
    const bodyRows = this.prioritizeCompleteRows(allBodyRows).slice(0, MAX_PREVIEW_ROWS);

    const headRowElement = document.createElement('tr');
    headerRow.forEach((cellValue) => {
      const cell = document.createElement('th');
      cell.scope = 'col';
      cell.textContent = cellValue;
      headRowElement.appendChild(cell);
    });
    tableHead.appendChild(headRowElement);

    bodyRows.forEach((row, rowIndex) => {
      const rowElement = document.createElement('tr');
      rowElement.dataset.previewExtraRow = rowIndex >= this.initialRows ? 'true' : 'false';
      rowElement.hidden = rowIndex >= this.initialRows;

      row.forEach((cellValue, cellIndex) => {
        const cell = document.createElement(cellIndex === 0 ? 'th' : 'td');
        if (cellIndex === 0) cell.scope = 'row';
        this.setCellContent(cell, cellValue);
        rowElement.appendChild(cell);
      });
      tableBody.appendChild(rowElement);
    });

    this.tableElement.hidden = false;
    if (this.tableWrapElement) this.tableWrapElement.hidden = false;
    if (this.toggleElement && bodyRows.length > this.initialRows) this.toggleElement.hidden = false;
  }

  setCellContent(cell, cellValue) {
    if (!this.isMaskedValue(cellValue)) {
      cell.textContent = cellValue;
      return;
    }

    cell.classList.add('custom-list-preview__cell--redacted');
    cell.setAttribute('aria-label', this.protectedLabel);
    const placeholder = document.createElement('span');
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.textContent = REDACTED_VALUE;
    cell.appendChild(placeholder);
  }

  prioritizeCompleteRows(rows) {
    return rows
      .map((row, index) => ({
        row,
        index,
        missingValues: row.filter((value) => this.isLegacyMissingValue(value)).length,
      }))
      .sort((first, second) => first.missingValues - second.missingValues || first.index - second.index)
      .map(({ row }) => row);
  }

  isLegacyMissingValue(value) {
    const normalizedValue = String(value ?? '').trim();
    return normalizedValue === '' || LEGACY_MISSING_VALUES.has(normalizedValue);
  }

  isMaskedValue(value) {
    const normalizedValue = String(value ?? '').trim();
    return (
      this.isLegacyMissingValue(normalizedValue) ||
      normalizedValue === REDACTED_VALUE ||
      LEGACY_REDACTED_VALUES.has(normalizedValue)
    );
  }

  toggleRows() {
    if (!this.toggleElement) return;
    const shouldExpand = this.toggleElement.getAttribute('aria-expanded') !== 'true';
    this.querySelectorAll('[data-preview-extra-row="true"]').forEach((row) => {
      row.hidden = !shouldExpand;
    });
    this.toggleElement.setAttribute('aria-expanded', String(shouldExpand));
    if (this.toggleLabelElement) {
      this.toggleLabelElement.textContent = shouldExpand ? this.collapseLabel : this.expandLabel;
    }
  }

  showLoading() {
    if (this.loadingElement) {
      this.loadingElement.hidden = false;
      this.loadingElement.textContent = this.loadingLabel;
    }
    if (this.noticeElement) this.noticeElement.hidden = true;
  }

  hideLoading() {
    if (this.loadingElement) this.loadingElement.hidden = true;
  }

  hideFeedback() {
    if (this.noticeElement) this.noticeElement.hidden = true;
  }

  showNotice(message) {
    if (this.noticeElement) this.noticeElement.hidden = false;
    if (this.noticeTextElement) this.noticeTextElement.textContent = message;
    if (this.tableElement) this.tableElement.hidden = true;
    if (this.tableWrapElement) this.tableWrapElement.hidden = true;
    if (this.toggleElement) this.toggleElement.hidden = true;
  }
}

if (!customElements.get('custom-list-preview')) {
  customElements.define('custom-list-preview', CustomListPreview);
}
